import { motion, useReducedMotion } from "framer-motion";
import React from "react";

/** Soft cinematic ease — presence without bounce. */
const EASE = [0.22, 1, 0.36, 1];

const OFFSETS = {
  up: { x: 0, y: 40 },
  down: { x: 0, y: -28 },
  left: { x: 48, y: 0 },
  right: { x: -48, y: 0 },
  scale: { x: 0, y: 28, scale: 0.96 },
  fade: { x: 0, y: 0 },
};

/**
 * Scroll-triggered reveal for Flashtoken sections.
 * Plays once; respects prefers-reduced-motion.
 */
const ScrollReveal = ({
  children,
  direction = "up",
  delay = 0,
  duration = 0.85,
  amount = 0.18,
  as = "div",
  className,
  style,
  ...rest
}) => {
  const reduceMotion = useReducedMotion();
  const Component = motion[as] || motion.div;
  const offset = OFFSETS[direction] || OFFSETS.up;

  if (reduceMotion) {
    return (
      <div className={className} style={style} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <Component
      className={className}
      style={{ willChange: "opacity, transform", ...style }}
      initial={{
        opacity: 0,
        x: offset.x,
        y: offset.y,
        scale: offset.scale ?? 1,
      }}
      whileInView={{
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1,
      }}
      viewport={{ once: true, amount }}
      transition={{
        duration,
        delay,
        ease: EASE,
      }}
      {...rest}
    >
      {children}
    </Component>
  );
};

/** Stagger container — children should be ScrollRevealItem. */
export const ScrollRevealGroup = ({
  children,
  stagger = 0.12,
  delay = 0,
  className,
  style,
  ...rest
}) => {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return (
      <div className={className} style={style} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      className={className}
      style={style}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      variants={{
        hidden: {},
        visible: {
          transition: { staggerChildren: stagger, delayChildren: delay },
        },
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

export const ScrollRevealItem = ({
  children,
  direction = "up",
  className,
  style,
  ...rest
}) => {
  const offset = OFFSETS[direction] || OFFSETS.up;

  return (
    <motion.div
      className={className}
      style={style}
      variants={{
        hidden: {
          opacity: 0,
          x: offset.x,
          y: offset.y,
          scale: offset.scale ?? 1,
        },
        visible: {
          opacity: 1,
          x: 0,
          y: 0,
          scale: 1,
          transition: { duration: 0.7, ease: EASE },
        },
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

export default ScrollReveal;
